import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, CheckCircle2, AlertCircle } from 'lucide-react';
import { authApi } from '../../utils/api';
import { mdpConforme } from '../../utils/motDePasse';
import ReglesMotDePasse from '../../components/ReglesMotDePasse';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const tooShort = password.length > 0 && !mdpConforme(password);
  const mismatch = confirm.length > 0 && password !== confirm;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!mdpConforme(password)) { setError('Le mot de passe ne respecte pas toutes les règles.'); return; }
    if (password !== confirm) { setError('Les deux mots de passe ne correspondent pas.'); return; }
    setLoading(true);
    try {
      await authApi.reset(token, password);
      setDone(true);
      setTimeout(() => navigate('/'), 2500);
    } catch (err) {
      setError(err.response?.data?.error || 'Le lien est invalide ou expiré.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-100 to-cream-200 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
            <span className="font-serif text-white text-3xl font-semibold">G</span>
          </div>
          <h1 className="font-serif text-2xl text-navy-900">Nouveau mot de passe</h1>
          <p className="text-navy-300 text-sm mt-0.5">Choisissez un mot de passe sécurisé</p>
        </div>

        {!token ? (
          <div className="bg-white rounded-[22px] shadow-xl p-6 text-center">
            <AlertCircle size={40} className="text-amber-500 mx-auto mb-3" />
            <p className="text-[14px] text-navy-700">Lien incomplet ou invalide. Refaites une demande de réinitialisation.</p>
            <Link to="/mot-de-passe-oublie" className="apple-btn-primary w-full justify-center mt-5">Refaire une demande</Link>
          </div>
        ) : done ? (
          <div className="bg-white rounded-[22px] shadow-xl p-6 text-center">
            <CheckCircle2 size={40} className="text-green-500 mx-auto mb-3" />
            <p className="text-[14px] text-navy-700">Votre mot de passe a été mis à jour. Redirection vers la connexion…</p>
            <Link to="/" className="apple-btn-primary w-full justify-center mt-5">Se connecter</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-[22px] shadow-xl p-6 space-y-4">
            <div>
              <label className="block text-[13px] font-medium text-navy-600 mb-1.5">Nouveau mot de passe</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-300" />
                <input
                  autoFocus
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 border border-cream-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-navy-300 focus:border-transparent"
                  placeholder="12 caractères minimum"
                />
              </div>
              <ReglesMotDePasse password={password} />
            </div>
            <div>
              <label className="block text-[13px] font-medium text-navy-600 mb-1.5">Confirmer le mot de passe</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-300" />
                <input
                  type="password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 border border-cream-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-navy-300 focus:border-transparent"
                  placeholder="retapez le mot de passe"
                />
              </div>
              {mismatch && <p className="text-[12px] text-amber-600 mt-1">Les mots de passe ne correspondent pas.</p>}
            </div>

            {error && <div className="text-[13px] text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}

            <button
              type="submit"
              disabled={loading || !password || !confirm || tooShort || mismatch}
              className="w-full bg-navy-800 hover:bg-navy-700 text-white py-2.5 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            >
              {loading ? 'Enregistrement…' : 'Valider le nouveau mot de passe'}
            </button>
          </form>
        )}

        <div className="flex items-center justify-center mt-6">
          <Link to="/" className="text-[13px] text-navy-400 hover:text-navy-700">Retour à la connexion</Link>
        </div>
      </div>
    </div>
  );
}
